<template>
  <section
    v-if="showCard"
    class="recent-activity"
    data-test="recent-activity"
  >
    <div class="recent-activity__header">
      <h2 class="recent-activity__title">Recent activity</h2>
      <router-link
        to="/activity"
        class="recent-activity__view-all"
        data-test="recent-activity-view-all"
      >
        View all activity
      </router-link>
    </div>

    <div
      v-if="isLoading"
      class="recent-activity__loading"
      data-test="recent-activity-loading"
    >
      <ExPrimarySpinner />
    </div>

    <div
      v-else-if="activities.length === 0"
      class="recent-activity__empty"
      data-test="recent-activity-empty"
    >
      <div class="recent-activity__empty-title">No activity yet.</div>
      <div class="recent-activity__empty-subtitle">
        Changes to Employees, Positions, Roles, Responsibilities and Departments will appear here.
      </div>
    </div>

    <div
      v-else
      class="recent-activity__list"
      data-test="recent-activity-list"
    >
      <ActivityStreamCard
        v-for="item in activities"
        :key="item.id"
        :actor_avatar="item.actor_avatar"
        :actor_name="item.actor_name"
        :object_type="item.object_type"
        :object_descriptor="item.object_descriptor"
        :verb="item.verb"
        :counterpart="item.counterpart"
        :datetime="item.datetime"
        :is_system_entry="item.is_system_entry"
        :is_legacy="item.is_legacy"
        :action_code="item.action_code"
        :subject_link="item.subject_link"
        :subject_name="item.subject_name"
        :subject_id="item.subject_id"
        :subject_label="item.subject_label"
        :counterpart_name="item.counterpart_name"
        :detail_text="item.detail_text"
        :is_system="item.is_system"
        :subject_href="item.subject_href"
        :counterpart_href="item.counterpart_href"
      />
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import ActivityStreamCard from "@components/ActivityStreamCard/ActivityStreamCard.vue";
import ExPrimarySpinner from "@components/common/ExPrimarySpinner.vue";
import { getAccountActivityStream } from "@/api";
import { transformActivityData } from "@/views/AccountActivity/AccountActivityHelpers";
import { normalizeAdminPermission } from "@/utils/defaultLoggedInRedirect";
import { LD_FLAG_IDEA_MDP_622 } from "@components/constants/launchDarklyFlagKeys";
import launchDarklyService from "@/services/launchdarkly";

const activities = ref([]);
const isLoading = ref(false);
const hasError = ref(false);
const isAdmin = ref(false);
const flagEnabled = ref(true);

// Card is visible only for elevated (admin) accounts, when the MDP-622 flag is
// not explicitly off, and while the feed request has not failed.
const showCard = computed(() => flagEnabled.value && isAdmin.value && !hasError.value);

onMounted(async () => {
  // Same admin gate as the Activity menu item (feed Rule 6): Administrator,
  // Co-Owner and Owner only. App Users / "None" never see the card.
  const accessPermission = JSON.parse(localStorage.getItem("accessPermission") || "{}");
  isAdmin.value = normalizeAdminPermission(accessPermission).hasElevatedNav;
  if (!isAdmin.value) {
    return;
  }

  try {
    // MDP-622 kill switch: renders by default and hides only when the flag
    // explicitly resolves false (avoids a flash in the normal, flag-unset case).
    flagEnabled.value = (await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_622, true)) !== false;
    if (!flagEnabled.value) {
      return;
    }

    isLoading.value = true;
    const accountId = JSON.parse(localStorage.getItem("currentAccountID") || "null");
    // Reuse the Activity page's data path: v2 feed + the shared transform.
    // Take the first page and render only its 3 newest entries.
    const { data } = await getAccountActivityStream(accountId, {});
    const transformed = transformActivityData(data && data.cards);
    activities.value = [...transformed]
      .sort((a, b) => b.createdOnMs - a.createdOnMs)
      .slice(0, 3);
  } catch (error) {
    // Error state: hide the card without breaking the page, and log.
    hasError.value = true;
    console.error("Failed to load recent activity:", error);
  } finally {
    isLoading.value = false;
  }
});
</script>

<style scoped>
.recent-activity {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
}

.recent-activity__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.recent-activity__title {
  margin: 0;
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.recent-activity__view-all {
  color: #7f56d9;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-decoration: none;
}

.recent-activity__view-all:hover,
.recent-activity__view-all:focus {
  text-decoration: underline;
  outline: none;
}

.recent-activity__view-all:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
  border-radius: 2px;
}

.recent-activity__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
}

.recent-activity__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 24px 0;
  text-align: center;
}

.recent-activity__empty-title {
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.recent-activity__empty-subtitle {
  color: #475467;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.recent-activity__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>
